import React, { useState, useMemo, useEffect } from 'react';
import { Search, User, CreditCard, Hash, Mail, Phone, MapPin, Loader2, AlertCircle, Info, ClipboardList, LogIn, Wallet, Settings2, CheckCircle, Target, Terminal } from 'lucide-react';
import { useConsumerSearch, useConsumerEnrollmentHistory, useConsumerLoginHistory, useCardValueLoads, useCardProxy, useConsumerCohort, useConsumerWallet, useCardTransactions, useConsumerTasks, useFISAddress, useMonetaryTransactionDetails, useTransactionSpendCategories, useDeclinedTransactions, useDeclinedTransactionDetails } from '../hooks/useReports';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import QueryInfoButton from '../components/QueryInfoButton';
import { CONSUMER_QUERIES } from '../constants/queries';
import { useAppAuth } from '../contexts/AuthContext';
import { formatDate, formatDateTime, formatChartDate } from '../utils/formatters';

function DetailCard({ title, icon: Icon, children, className = '' }) {
  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
        <h3 className="font-medium text-gray-900 flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary-600" />
          {title}
        </h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function DetailRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start py-2 border-b border-gray-50 last:border-0">
      <div className="flex items-center gap-2 w-32 sm:w-40 flex-shrink-0">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <span className="text-sm font-medium text-gray-900 break-all">
        {value ?? <span className="text-gray-400 italic">N/A</span>}
      </span>
    </div>
  );
}

function TxnSep() {
  return <div className="h-3 bg-gray-200 border-y border-gray-300" />;
}

function TxnRow3({ label, issuing, local }) {
  return (
    <div className="grid px-4 py-2 border-b border-gray-50 hover:bg-amber-50 text-sm" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
      <span className="font-semibold text-gray-700">{label}:</span>
      <span className="text-gray-600">{issuing}</span>
      <span className="text-gray-600">{local}</span>
    </div>
  );
}

function TxnRow2({ l1, v1, l2, v2 }) {
  return (
    <div className="grid grid-cols-2 px-4 py-2 border-b border-gray-50 hover:bg-amber-50 text-sm gap-2">
      <div><span className="font-semibold text-gray-700">{l1}: </span><span className="text-gray-600 break-all">{v1}</span></div>
      <div><span className="font-semibold text-gray-700">{l2}: </span><span className="text-gray-600 break-all">{v2}</span></div>
    </div>
  );
}

function TxnRow1({ label, value }) {
  return (
    <div className="px-4 py-2 border-b border-gray-50 hover:bg-amber-50 text-sm">
      <span className="font-semibold text-gray-700">{label}: </span>
      <span className="text-gray-600 break-all">{value}</span>
    </div>
  );
}

function TransactionDetailsModal({ txnUid, type = 'approved', cardProxy, onClose }) {
  // Both hooks are always called (rules of hooks), but only one is ever
  // active — the other receives undefined for txnUid, which its own
  // `enabled` check already short-circuits into a no-op, no network call.
  // getDeclinedTransactionDetails' columns are deliberately aliased to the
  // same shape as getMonetaryTransactionDetails, so the rest of this modal
  // needs no changes to support either.
  const approvedResult = useMonetaryTransactionDetails(type === 'approved' ? txnUid : undefined);
  const declinedResult = useDeclinedTransactionDetails(type === 'declined' ? txnUid : undefined, type === 'declined' ? cardProxy : undefined);
  const { data, isLoading, isError } = type === 'declined' ? declinedResult : approvedResult;
  // A declined transaction never has purchase_item_details rows (nothing
  // was ever purchased), so skip this lookup entirely for that case.
  const { data: spendCatData } = useTransactionSpendCategories(type === 'approved' ? txnUid : undefined);
  const amtRequested = spendCatData?.data?.length
    ? spendCatData.data.map(r => `${r.benefit_type}-${parseFloat(r.amount).toFixed(2)}`).join(', ')
    : null;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  const txn = data?.data?.[0];

  const nd = <span className="text-gray-400 italic">no data</span>;
  const v = (x) => (x != null && x !== '') ? String(x) : nd;
  const usd = (amount, curr) =>
    amount != null ? `${curr || 'USD'} ${parseFloat(amount).toFixed(2)}` : nd;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-lg shadow-2xl flex flex-col w-full max-w-2xl border border-gray-200" style={{ maxHeight: '90vh' }}>

        {/* Amber gradient header — matches consumer page banner */}
        <div className="flex items-center justify-between px-4 py-3 rounded-t-lg flex-shrink-0" style={{ background: 'linear-gradient(to right, #d97706, #f59e0b)' }}>
          <div className="flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="30" height="30">
              <circle cx="50" cy="50" r="50" fill="rgba(255,255,255,0.25)" />
              <text x="50" y="62" textAnchor="middle" fontFamily="Arial,sans-serif" fontWeight="bold" fontSize="48" fill="white">S</text>
              <circle cx="50" cy="14" r="3.5" fill="white" /><circle cx="38" cy="16" r="3" fill="white" />
              <circle cx="62" cy="16" r="3" fill="white" /><circle cx="28" cy="22" r="2.5" fill="white" />
              <circle cx="72" cy="22" r="2.5" fill="white" /><circle cx="20" cy="30" r="2" fill="white" />
              <circle cx="80" cy="30" r="2" fill="white" />
            </svg>
            <div>
              <div className="text-white font-bold text-sm">{type === 'declined' ? 'Declined Transaction Details' : 'Transaction Details'}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl leading-none px-1">✕</button>
        </div>

        {/* 3-column sub-header */}
        <div className="grid flex-shrink-0 px-4 py-2 bg-gray-100 border-b border-gray-300 text-xs font-bold text-gray-600 uppercase tracking-wide" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
          <span>Details</span>
          <span>Issuing Currency</span>
          <span>Local Currency</span>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 scrollbar-thin">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-amber-500 mr-2" />
              <span className="text-gray-500 text-sm">Loading transaction details...</span>
            </div>
          )}
          {isError && (
            <div className="flex items-center gap-2 m-4 p-3 bg-red-50 rounded-lg border border-red-100">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-red-600 text-sm">Failed to load transaction details</span>
            </div>
          )}
          {txn && (
            <>
              {/* Financial Amounts */}
              <TxnRow3 label="Country" issuing={v(txn.merchant_country_name || txn.merchant_country_code)} local={v(txn.merchant_country_name || txn.merchant_country_code)} />
              <TxnRow3 label="Currency" issuing={v(txn.bin_currency_alpha)} local={v(txn.transaction_currency_alpha)} />
              <TxnRow3 label="Merchant Auth Amt" issuing={usd(txn.authorization_amount, txn.bin_currency_alpha)} local={usd(txn.authorization_amount, txn.transaction_currency_alpha)} />
              <TxnRow3 label="Total Settle Amt" issuing={usd(txn.settle_amount, txn.bin_currency_alpha)} local={usd(txn.settle_amount, txn.transaction_currency_alpha)} />
              <TxnRow3 label="Variance" issuing={v(txn.variance)} local={v(txn.variance)} />

              <TxnSep />

              {/* Dates & Card */}
              <div className="grid px-4 py-2 border-b border-gray-50 hover:bg-amber-50 text-sm gap-2" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
                <div><span className="font-semibold text-gray-700">System Date: </span><span className="text-gray-600 text-xs">{v(txn.wcs_utc_inserted ? formatDateTime(txn.wcs_utc_inserted) : null)}</span></div>
                <div><span className="font-semibold text-gray-700">Trans Date: </span><span className="text-gray-600 text-xs">{v(txn.txn_loc_datetime ? formatDateTime(txn.txn_loc_datetime) : null)}</span></div>
                <div><span className="font-semibold text-gray-700">Local Post Date: </span><span className="text-gray-600 text-xs">{v(txn.wcs_local_post_date ? formatDateTime(txn.wcs_local_post_date) : null)}</span></div>
                <div><span className="font-semibold text-gray-700">Post Date: </span><span className="text-gray-600 text-xs">{v(txn.wcs_utc_post_date ? formatDateTime(txn.wcs_utc_post_date) : null)}</span></div>
              </div>
              <TxnRow2 l1="Card Number" v1={<span className="font-mono text-xs">{v(txn.card_number)}</span>} l2="Purse Exp Date" v2={v(txn.purse_expiration_date)} />
              <TxnRow2 l1="Source" v1={v(txn.source_description)} l2="Reversed" v2={v(txn.reversed != null ? (String(txn.reversed) === '1' ? 'Yes' : 'No') : null)} />

              <TxnSep />

              {/* Transaction Info */}
              <TxnRow2 l1="Request Type" v1={v(txn.actual_request_code_description)} l2="Retrieval / Reference" v2={<span className="font-mono text-xs">{v(txn.retrieval_ref_no)}</span>} />
              <TxnRow2 l1="Response" v1={v(txn.response_description)} l2="Result" v2={v(txn.authorization_response)} />

              <TxnSep />

              {/* Merchant */}
              <TxnRow2 l1="Merchant Name" v1={v(txn.merchant_name)} l2="Authorization Code" v2={<span className="font-mono text-xs">{v(txn.authorization_code)}</span>} />
              <TxnRow2 l1="Merchant Number" v1={<span className="font-mono text-xs">{v(txn.merchant_number)}</span>} l2="AVS Information" v2={v(txn.avs_information)} />
              <TxnRow1 label="Original Merchant Number" value={<span className="font-mono text-xs">{v(txn.original_merchant_number)}</span>} />
              <TxnRow2 l1="Terminal Number" v1={<span className="font-mono text-xs">{v(txn.terminal_number)}</span>} l2="AVS Response" v2={v(txn.address_verification_response)} />
              <TxnRow2 l1="Client Reference" v1={v(txn.client_reference_number)} l2="Purse" v2={v(txn.purse_name)} />
              <TxnRow2 l1="POS Entry" v1={v(txn.pos_entry_description)} l2="Match Status" v2={v(txn.match_status_description)} />
              <TxnRow2
                l1="MCC"
                v1={txn.mcc && txn.mcc_description ? `(${txn.mcc}) - ${txn.mcc_description}` : v(txn.mcc_description || txn.mcc)}
                l2="Match Type"
                v2={v(txn.match_type_description)}
              />

              <TxnSep />

              {/* Reference IDs */}
              <TxnRow1 label="Transaction ID Number" value={<span className="font-mono text-xs">{v(txn.txn_uid)}</span>} />
              <TxnRow1 label="Acquirer Reference Number" value={v(txn.acquirer_reference_number)} />
              <TxnRow1 label="Spend Category-Amt Requested" value={v(amtRequested ?? txn.spend_category)} />
              {type !== 'declined' && (
                <TxnRow1 label="Spend Category-Amt Approved" value={v(txn.spend_category)} />
              )}

              <TxnSep />

              {/* Audit */}
              <TxnRow2 l1="User ID" v1={v(txn.user_id)} l2="Reason" v2={v(txn.reason_description)} />
              <TxnRow1 label="Comment" value={v(txn.comment)} />
            </>
          )}
        </div>

      </div>
    </div>
  );
}

export default function ConsumerQueries({ onNavigate }) {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTxnUid, setSelectedTxnUid] = useState(null);
  const [selectedDeclinedTxnUid, setSelectedDeclinedTxnUid] = useState(null);
  const [selectedConsumerIndex, setSelectedConsumerIndex] = useState(0);
  const [cardTxnTab, setCardTxnTab] = useState('all');
  const [purseFilter, setPurseFilter] = useState('all');
  const [cardFilter, setCardFilter] = useState('all');

  const { hasPermission } = useAppAuth();
  const { data, isLoading, isError, error, isSuccess } = useConsumerSearch(searchTerm);

  const consumer = data?.data?.[selectedConsumerIndex];

  const consumerCodeForReset = consumer?.consumer_code || '';
  useEffect(() => {
    setCardFilter('all');
    setPurseFilter('all');
    setCardTxnTab('all');
  }, [consumerCodeForReset]);
  const memNbr = consumer?.mem_nbr || '';

  const {
    data: enrollmentData,
    isLoading: enrollmentLoading,
    isError: enrollmentError,
  } = useConsumerEnrollmentHistory(memNbr);

  const consumerId = consumer?.consumer_id || '';

  const {
    data: loginData,
    isLoading: loginLoading,
    isError: loginError,
  } = useConsumerLoginHistory(consumerId);

  const consumerCode = consumer?.consumer_code || '';

  const {
    data: cardValueData,
    isLoading: cardValueLoading,
    isError: cardValueError,
  } = useCardValueLoads(consumerCode);

  const {
    data: cardProxyData,
    isLoading: cardProxyLoading,
    isError: cardProxyError,
  } = useCardProxy(consumerCode);

  const cardProxy = cardProxyData?.data?.[0];



  const {
    data: cohortData,
    isLoading: cohortLoading,
    isError: cohortError,
  } = useConsumerCohort(consumerCode);

  const {
    data: walletData,
    isLoading: walletLoading,
    isError: walletError,
  } = useConsumerWallet(consumerCode);

  const cardNumberProxy = cardProxy?.card_number_proxy || '';

  const {
    data: cardTransactionsData,
    isLoading: cardTransactionsLoading,
    isError: cardTransactionsError,
  } = useCardTransactions(consumerCode);

  const {
    data: declinedTxnData,
    isLoading: declinedTxnLoading,
    isError: declinedTxnError,
  } = useDeclinedTransactions(cardNumberProxy);

  const {
    data: tasksData,
    isLoading: tasksLoading,
    isError: tasksError,
  } = useConsumerTasks(consumerCode);

  const {
    data: fisAddressData,
    isLoading: fisAddressLoading,
    isError: fisAddressError,
  } = useFISAddress(cardNumberProxy);

  // Process login data for chart - aggregate by date
  const loginChartData = useMemo(() => {
    if (!loginData?.data || loginData.data.length === 0) return [];

    // Group logins by date
    const loginsByDate = {};
    loginData.data.forEach((login) => {
      const date = login.login_dt ? new Date(login.login_dt).toISOString().split('T')[0] : null;
      if (date) {
        loginsByDate[date] = (loginsByDate[date] || 0) + 1;
      }
    });

    // Convert to array and sort by date
    return Object.entries(loginsByDate)
      .map(([date, count]) => ({
        date,
        displayDate: formatChartDate(date),
        logins: count,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-30); // Show last 30 days
  }, [loginData]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSelectedConsumerIndex(0);
      setSearchTerm(searchInput.trim());
    }
  };

  // formatDate and formatDateTime are imported from utils/formatters with EST timezone

  return (
    <div className="space-y-6">
      {selectedTxnUid && (
        <TransactionDetailsModal txnUid={selectedTxnUid} onClose={() => setSelectedTxnUid(null)} />
      )}
      {selectedDeclinedTxnUid && (
        <TransactionDetailsModal
          txnUid={selectedDeclinedTxnUid}
          type="declined"
          cardProxy={cardNumberProxy}
          onClose={() => setSelectedDeclinedTxnUid(null)}
        />
      )}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3 sm:gap-4">
          <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-amber-900 text-sm sm:text-base">Consumer Query Interface</h4>
            <p className="text-xs sm:text-sm text-amber-700 mt-1">
              Search for consumer details using Member Number (mem_nbr) or Member ID.
              Results will display comprehensive consumer information in organized sections.
            </p>
          </div>
        </div>
        <QueryInfoButton
          title="Consumer Query Data Sources"
          queries={[
            CONSUMER_QUERIES.search,
            CONSUMER_QUERIES.enrollmentHistory,
            CONSUMER_QUERIES.loginHistory,
            CONSUMER_QUERIES.cardValueLoads,
            CONSUMER_QUERIES.cardProxy,
            CONSUMER_QUERIES.cohort,
            CONSUMER_QUERIES.wallet,
            CONSUMER_QUERIES.cardTransactions,
            CONSUMER_QUERIES.tasks,
          ]}
        />
      </div>

      {/* Search Section */}
      <div className="card">
        <h3 className="card-header flex items-center gap-2">
          <Search className="w-5 h-5 text-primary-600" />
          Consumer Search
        </h3>
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Enter Member Number, Member ID or Email..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            type="submit"
            disabled={!searchInput.trim() || isLoading}
            className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Search
              </>
            )}
          </button>
        </form>
      </div>

      {/* Error State */}
      {isError && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-700">{error?.message || 'Failed to search consumer'}</span>
        </div>
      )}

      {/* No Results */}
      {isSuccess && (!data?.data || data.data.length === 0) && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <span className="text-amber-700">No consumer found matching "{searchTerm}"</span>
        </div>
      )}

      {/* Multiple Results Selector */}
      {isSuccess && data?.data?.length > 1 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <span className="text-amber-800 font-medium text-sm">
              {data.data.length} consumers found — select one to view details
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.data.map((c, idx) => (
              <button
                key={c.consumer_code || idx}
                onClick={() => setSelectedConsumerIndex(idx)}
                className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                  selectedConsumerIndex === idx
                    ? 'bg-amber-600 text-white border-amber-600 font-medium'
                    : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'
                }`}
              >
                {c.first_name} {c.last_name}
                <span className="ml-2 opacity-75 text-xs">{c.consumer_code}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Consumer Details */}
      {isSuccess && consumer && (
        <div className="space-y-6">
          {/* Header with Consumer Name */}
          <div className="bg-gradient-to-r from-primary-600 to-amber-600 rounded-lg p-4 sm:p-6 text-white">
            <div className="flex items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 sm:w-8 sm:h-8" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold truncate">
                    {consumer.first_name} {consumer.last_name}
                  </h2>
                  <p className="text-primary-100 mt-1 text-xs sm:text-sm truncate">
                    Member ID: {consumer.member_id || 'N/A'} | Member #: {consumer.mem_nbr || 'N/A'}
                  </p>
                </div>
              </div>
              {hasPermission('logs:consumer-search') && onNavigate && (
                <button
                  onClick={() => {
                    sessionStorage.setItem('logSearchPrefill', JSON.stringify({
                      keyword: consumerCode,
                      selectAllGroups: true,
                      timePresetIndex: 3,
                      autoSearch: true,
                    }));
                    onNavigate('tech-support');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors flex-shrink-0"
                >
                  <Terminal className="w-4 h-4" />
                  Show Logs
                </button>
              )}
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <DetailCard title="Basic Information" icon={User}>
              <DetailRow label="Consumer Code" value={consumer.consumer_code} icon={Hash} />
              <DetailRow label="Member ID" value={consumer.member_id} icon={Hash} />
              <DetailRow label="Member Number" value={consumer.mem_nbr} icon={Hash} />
              <DetailRow label="First Name" value={consumer.first_name} icon={User} />
              <DetailRow label="Last Name" value={consumer.last_name} icon={User} />
              <DetailRow label="Email" value={consumer.email} icon={Mail} />
              <DetailRow label="Phone" value={consumer.phone_number || consumer.home_phone_number} icon={Phone} />
              <DetailRow label="Tenant Name" value={consumer.tenant_name} icon={Hash} />
              <DetailRow label="Tenant Code" value={consumer.tenant_code} icon={Hash} />
            </DetailCard>

            {/* Advanced Information - Cohort & Wallet */}
            <DetailCard title="Advanced Information" icon={Settings2}>
              {/* Cohort Section */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Consumer Cohort</h4>
                {cohortLoading ? (
                  <div className="flex items-center py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary-600 mr-2" />
                    <span className="text-sm text-gray-500">Loading cohort data...</span>
                  </div>
                ) : cohortError ? (
                  <div className="flex items-center gap-2 py-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-red-600">Failed to load cohort data</span>
                  </div>
                ) : cohortData?.data?.length === 0 ? (
                  <div className="flex items-center gap-2 py-2 bg-gray-50 rounded p-2">
                    <Info className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">No cohort data found</span>
                  </div>
                ) : (
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {cohortData?.data?.map((cohort, idx) => (
                      <div key={idx} className="text-sm bg-amber-50 rounded px-2 py-1 border border-amber-100">
                        <span className="font-medium text-amber-800">Cohort {cohort.cohort_id}</span>
                        {cohort.cohort_name && <span className="text-amber-600 ml-2">- {cohort.cohort_name}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Wallet Section */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Consumer Wallet</h4>
                {walletLoading ? (
                  <div className="flex items-center py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary-600 mr-2" />
                    <span className="text-sm text-gray-500">Loading wallet data...</span>
                  </div>
                ) : walletError ? (
                  <div className="flex items-center gap-2 py-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-red-600">Failed to load wallet data</span>
                  </div>
                ) : walletData?.data?.length === 0 ? (
                  <div className="flex items-center gap-2 py-2 bg-gray-50 rounded p-2">
                    <Info className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">No wallet data found</span>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {walletData?.data?.map((wallet, idx) => (
                      <div key={idx} className="text-sm bg-green-50 rounded px-3 py-1 border border-green-100">
                        <span className="font-medium text-green-800">{wallet.wallet_type_label || 'Unknown'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DetailCard>

            {/* Account Status */}
            <DetailCard title="Account Status" icon={CreditCard}>
              {cardProxyLoading ? (
                <div className="flex items-center py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-primary-600 mr-2" />
                  <span className="text-sm text-gray-500">Loading card info...</span>
                </div>
              ) : cardProxyError ? (
                <div className="flex items-center gap-2 py-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-600">Failed to load card info</span>
                </div>
              ) : (
                <>
                  <DetailRow label="Proxy Number" value={cardProxy?.card_number_proxy} icon={CreditCard} />
                </>
              )}

              <DetailRow label="Client Unique ID" value={cardProxy?.client_unique_id}  />
              <DetailRow label="Is Active" value={consumer.eligible === 'true' ? 'Yes' : 'No'} />
              <DetailRow label="Deleted" value={consumer.deleted_consumer_key ? 'Yes' : 'No'} />
              <DetailRow label="Onboarded" value={consumer.onboarding_state} />
              <DetailRow label="Agreement Status" value={consumer.agreement_status} />
              <DetailRow label="Region Code" value={consumer.region} />
              <DetailRow label="Card Last Four" value={cardProxy?.card_last_four ? `****${cardProxy.card_last_four}` : undefined} />

            </DetailCard>

            {/* Card Value Loads */}
            <DetailCard title="Card Value Loads" icon={Wallet}>
              {cardValueLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-500 text-sm">Loading...</span>
                </div>
              )}
              {cardValueError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-red-700 text-sm">Failed to load card value loads</span>
                </div>
              )}
              {!cardValueLoading && !cardValueError && cardValueData?.data && (
                cardValueData.data.length === 0 ? (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <Info className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600 text-sm">No card value loads found</span>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {cardValueData.data.map((load, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-gray-900 text-sm">{load.purse_name || 'N/A'}</span>
                          <span className="font-bold text-green-600">${parseFloat(load.total_dollars || 0).toFixed(2)}</span>
                        </div>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>{load.transaction_description || 'No description'}</div>
                          <div className="text-gray-400">{load.transaction_type_detail || 'N/A'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </DetailCard>

            {/* Address Information */}
            <DetailCard title="Address Information" icon={MapPin} className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <div>
                  <DetailRow label="Address Line 1" value={consumer.mailing_addr_line_1} icon={MapPin} />
                  <DetailRow label="Address Line 2" value={consumer.mailing_addr_line_2} />
                  <DetailRow label="City" value={consumer.city} />
                </div>
                <div>
                  <DetailRow label="State" value={consumer.mailing_state} />
                  <DetailRow label="Zip Code" value={consumer.postal_code} />
                  <DetailRow label="Country" value={consumer.country} />
                </div>
              </div>
            </DetailCard>

            {/* Address in FIS */}
            <DetailCard title="Address in FIS" icon={MapPin} className="lg:col-span-2">
              {fisAddressLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-500 text-sm">Loading FIS address...</span>
                </div>
              ) : fisAddressError ? (
                <div className="flex items-center gap-2 py-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-600">Failed to load FIS address</span>
                </div>
              ) : !fisAddressData?.data?.[0] ? (
                <div className="flex items-center gap-2 py-2 bg-gray-50 rounded p-2">
                  <Info className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">No FIS address found</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  <div>
                    <DetailRow label="Address Line 1" value={fisAddressData.data[0].address_line1} icon={MapPin} />
                    <DetailRow label="Address Line 2" value={fisAddressData.data[0].address_line2} />
                    <DetailRow label="City" value={fisAddressData.data[0].city} />
                  </div>
                  <div>
                    <DetailRow label="State" value={fisAddressData.data[0].state} />
                    <DetailRow label="Zip Code" value={fisAddressData.data[0].zip_code} />
                    <DetailRow label="Country" value={fisAddressData.data[0].country} />
                  </div>
                </div>
              )}
            </DetailCard>

            {/* Card Transactions */}
            <DetailCard title="Card Transactions" icon={CreditCard} className="lg:col-span-2">
              {cardProxyLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-500 text-sm">Loading card info...</span>
                </div>
              ) : !cardProxy?.card_number_proxy ? (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Info className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600 text-sm">No card proxy found for this consumer</span>
                </div>
              ) : (
                <>
                  <div className="flex gap-1 mb-4 border-b border-gray-200">
                    {['all', 'declined'].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setCardTxnTab(tab)}
                        className={`px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px transition-colors ${
                          cardTxnTab === tab
                            ? 'text-amber-700 border-amber-500 bg-amber-50'
                            : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab === 'all' ? 'All' : 'Declined'}
                      </button>
                    ))}
                  </div>

                  {cardTxnTab === 'all' && (
                    cardTransactionsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                        <span className="ml-2 text-gray-500 text-sm">Loading transactions...</span>
                      </div>
                    ) : cardTransactionsError ? (
                      <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <span className="text-red-700 text-sm">Failed to load card transactions</span>
                      </div>
                    ) : !cardTransactionsData?.data?.length ? (
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <Info className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600 text-sm">No card transactions found</span>
                      </div>
                    ) : (
                      <>
                        {(() => {
                          const cardOptions = ['all', ...Array.from(new Set(cardTransactionsData.data.map(t => t.card_last_four).filter(Boolean))).sort()];
                          const cardFilteredTxns = cardFilter === 'all' ? cardTransactionsData.data : cardTransactionsData.data.filter(t => t.card_last_four === cardFilter);
                          const purseOptions = ['all', ...Array.from(new Set(cardFilteredTxns.map(t => t.purse_name).filter(Boolean))).sort()];
                          const filteredTxns = purseFilter === 'all' ? cardFilteredTxns : cardFilteredTxns.filter(t => t.purse_name === purseFilter);
                          return (
                            <>
                              <div className="flex items-center gap-3 mb-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Card</span>
                                  <select
                                    value={cardFilter}
                                    onChange={e => { setCardFilter(e.target.value); setPurseFilter('all'); }}
                                    className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                                  >
                                    <option value="all">All cards</option>
                                    {cardOptions.filter(c => c !== 'all').map(c => (
                                      <option key={c} value={c}>Card ending {c}</option>
                                    ))}
                                  </select>
                                </div>
                                <span className="text-gray-300">|</span>
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Purse</span>
                                {purseOptions.map(p => (
                                  <button
                                    key={p}
                                    onClick={() => setPurseFilter(p)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                      purseFilter === p
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-white border border-gray-300 text-gray-600 hover:border-amber-400 hover:text-amber-700'
                                    }`}
                                  >
                                    {p === 'all' ? 'All' : p}
                                  </button>
                                ))}
                              </div>
                              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Card</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date/Time</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Merchant</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purse</th>
                                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type Detail</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredTxns.map((txn, idx) => (
                                      <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap font-mono">{txn.card_last_four ? `••${txn.card_last_four}` : <span className="text-gray-400 italic">N/A</span>}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{txn.txn_local_datetime ? formatDateTime(txn.txn_local_datetime) : <span className="text-gray-400 italic">N/A</span>}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{txn.merchant_name || <span className="text-gray-400 italic">N/A</span>}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{txn.purse_name || <span className="text-gray-400 italic">N/A</span>}</td>
                                        <td className="px-4 py-3 text-sm text-right font-medium text-green-600">${parseFloat(txn.txn_total_dollars || 0).toFixed(2)}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{txn.transaction_type || <span className="text-gray-400 italic">N/A</span>}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{txn.transaction_type_detail || <span className="text-gray-400 italic">N/A</span>}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{txn.transaction_description || <span className="text-gray-400 italic">N/A</span>}</td>
                                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                                          <button
                                            onClick={() => setSelectedTxnUid(txn.txn_uid)}
                                            className="text-xs px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100 font-medium"
                                          >
                                            View Details
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )
                  )}

                  {cardTxnTab === 'declined' && (
                    declinedTxnLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                        <span className="ml-2 text-gray-500 text-sm">Loading declined transactions...</span>
                      </div>
                    ) : declinedTxnError ? (
                      <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <span className="text-red-700 text-sm">Failed to load declined transactions</span>
                      </div>
                    ) : !declinedTxnData?.data?.length ? (
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <Info className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600 text-sm">No declined transactions found</span>
                      </div>
                    ) : (
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date/Time</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Merchant</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purse</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {declinedTxnData.data.map((txn, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{txn.transaction_datetime ? formatDateTime(txn.transaction_datetime) : <span className="text-gray-400 italic">N/A</span>}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{txn.merchant_name || <span className="text-gray-400 italic">N/A</span>}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{[txn.merchant_city, txn.merchant_state].filter(Boolean).join(', ') || <span className="text-gray-400 italic">N/A</span>}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{txn.purse_name || <span className="text-gray-400 italic">N/A</span>}</td>
                                <td className="px-4 py-3 text-sm text-right font-medium text-red-600">${parseFloat(txn.amount || 0).toFixed(2)}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{txn.response_desc || txn.reason_code_desc || <span className="text-gray-400 italic">N/A</span>}</td>
                                <td className="px-4 py-3 text-sm whitespace-nowrap">
                                  <button
                                    onClick={() => setSelectedDeclinedTxnUid(txn.txn_uid)}
                                    className="text-xs px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100 font-medium"
                                  >
                                    View Details
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </>
              )}
            </DetailCard>

            {/* Consumer Tasks */}
            <DetailCard title="Consumer Tasks" icon={Target} className="lg:col-span-2">
              {tasksLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-500 text-sm">Loading consumer tasks...</span>
                </div>
              ) : tasksError ? (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-red-700 text-sm">Failed to load consumer tasks</span>
                </div>
              ) : tasksData?.data?.length === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Info className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600 text-sm">No tasks found for this consumer</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Task Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-amber-50 rounded-lg p-3">
                      <div className="text-xl font-bold text-amber-700">{tasksData?.data?.length || 0}</div>
                      <div className="text-xs text-amber-600">Total Tasks</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <div className="text-xl font-bold text-green-700">
                        {tasksData?.data?.filter(t => t.task_status?.toLowerCase() === 'completed').length || 0}
                      </div>
                      <div className="text-xs text-green-600">Completed</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-3">
                      <div className="text-xl font-bold text-yellow-700">
                        {tasksData?.data?.filter(t => ['in progress', 'in_progress', 'enrolled'].includes(t.task_status?.toLowerCase())).length || 0}
                      </div>
                      <div className="text-xs text-yellow-600">In Progress</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="text-xl font-bold text-purple-700">
                        ${tasksData?.data?.reduce((sum, t) => sum + parseFloat(t.reward_amount || 0), 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-purple-600">Total Rewards</div>
                    </div>
                  </div>

                  {/* Tasks Table */}
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">External Code</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Start</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reward</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tasksData?.data?.map((task, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <div className="font-medium">{task.task_name || <span className="text-gray-400 italic">N/A</span>}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{task.task_external_code || <span className="text-gray-400 italic">N/A</span>}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                task.task_status === 'Completed' ? 'bg-green-100 text-green-700' :
                                task.task_status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' :
                                task.task_status === 'Enrolled' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {task.task_status === 'Completed' && <CheckCircle className="w-3 h-3" />}
                                {task.task_status || 'Unknown'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                              {task.valid_start_ts ? formatDateTime(task.valid_start_ts) : <span className="text-gray-400 italic">N/A</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                              {task.task_completed_ts ? formatDateTime(task.task_completed_ts) : <span className="text-gray-400 italic">N/A</span>}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {task.reward_amount ? (
                                <div>
                                  <span className="font-medium text-green-600">
                                    ${parseFloat(task.reward_amount).toFixed(2)}
                                  </span>
                                  {task.reward_type && (
                                    <div className="text-xs text-gray-500">{task.reward_type}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 italic">No reward</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </DetailCard>
          </div>

          {/* Enrollment History Section */}
          <div className="card">
            <h3 className="card-header flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary-600" />
              Enrollment Status History
            </h3>
            {enrollmentLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                <span className="ml-2 text-gray-500">Loading enrollment history...</span>
              </div>
            )}
            {enrollmentError && (
              <div className="flex items-center gap-2 p-4 bg-red-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-red-700">Failed to load enrollment history</span>
              </div>
            )}
            {!enrollmentLoading && !enrollmentError && enrollmentData?.data && (
              enrollmentData.data.length === 0 ? (
                <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg">
                  <Info className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-600">No enrollment history found for this member</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {enrollmentData.data[0] && Object.keys(enrollmentData.data[0]).map((key) => (
                          <th
                            key={key}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {key.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {enrollmentData.data.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          {Object.entries(row).map(([key, value]) => (
                            <td key={key} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                              {value !== null && value !== undefined ? (
                                key.toLowerCase().includes('date') || key.toLowerCase().includes('_ts') ? (
                                  formatDate(value)
                                ) : (
                                  String(value)
                                )
                              ) : (
                                <span className="text-gray-400 italic">null</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>

          {/* Login History Section */}
          <div className="card">
            <h3 className="card-header flex items-center gap-2">
              <LogIn className="w-5 h-5 text-primary-600" />
              Consumer Login History
            </h3>
            {loginLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                <span className="ml-2 text-gray-500">Loading login history...</span>
              </div>
            )}
            {loginError && (
              <div className="flex items-center gap-2 p-4 bg-red-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-red-700">Failed to load login history</span>
              </div>
            )}
            {!loginLoading && !loginError && loginData?.data && (
              loginData.data.length === 0 ? (
                <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg">
                  <Info className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-600">No login history found for this consumer</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-amber-50 rounded-lg p-3 sm:p-4">
                      <div className="text-xl sm:text-2xl font-bold text-amber-700">{loginData.data.length}</div>
                      <div className="text-xs sm:text-sm text-amber-600">Total Logins</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                      <div className="text-xl sm:text-2xl font-bold text-green-700">{loginChartData.length}</div>
                      <div className="text-xs sm:text-sm text-green-600">Days with Activity</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                      <div className="text-xl sm:text-2xl font-bold text-purple-700">
                        {loginData.data[0]?.login_dt ? formatDate(loginData.data[0].login_dt) : 'N/A'}
                      </div>
                      <div className="text-xs sm:text-sm text-purple-600">Last Login</div>
                    </div>
                  </div>

                  {/* Login Activity Chart */}
                  {loginChartData.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-4">Login Activity (Last 30 Days)</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={loginChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis
                              dataKey="displayDate"
                              tick={{ fontSize: 12 }}
                              tickLine={false}
                              axisLine={{ stroke: '#e5e7eb' }}
                            />
                            <YAxis
                              tick={{ fontSize: 12 }}
                              tickLine={false}
                              axisLine={{ stroke: '#e5e7eb' }}
                              allowDecimals={false}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                              }}
                              formatter={(value) => [value, 'Logins']}
                              labelFormatter={(label) => `Date: ${label}`}
                            />
                            <Bar dataKey="logins" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Login Details Table */}
                  <details className="border border-gray-200 rounded-lg">
                    <summary className="cursor-pointer p-4 font-medium text-gray-700 hover:text-primary-600 bg-gray-50 rounded-t-lg">
                      View All Login Records ({loginData.data.length})
                    </summary>
                    <div className="overflow-x-auto max-h-64 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            {loginData.data[0] && Object.keys(loginData.data[0]).map((key) => (
                              <th
                                key={key}
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {key.replace(/_/g, ' ')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {loginData.data.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              {Object.entries(row).map(([key, value]) => (
                                <td key={key} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                  {value !== null && value !== undefined ? (
                                    key.toLowerCase().includes('date') || key.toLowerCase().includes('_dt') || key.toLowerCase().includes('_ts') ? (
                                      formatDateTime(value)
                                    ) : (
                                      String(value)
                                    )
                                  ) : (
                                    <span className="text-gray-400 italic">null</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              )
            )}
          </div>

          {/* Raw Data Section (Collapsible) */}
          <details className="card">
            <summary className="cursor-pointer font-medium text-gray-700 hover:text-primary-600">
              View Raw Data (All Fields)
            </summary>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(consumer).map(([key, value]) => (
                    <tr key={key} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{key}</td>
                      <td className="px-4 py-2 text-sm text-gray-600 break-all">
                        {value !== null && value !== undefined ? String(value) : <span className="text-gray-400 italic">null</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
